import moment from 'moment-timezone';
import 'moment-timezone/moment-timezone-utils';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectBuildingTimeZone } from 'store/building/selectors';

const FIXED_OFFSET_REGEX = /^([+-])([01]\d|2[0-3]):([0-5]\d)$/;

// Moment stores offsets in minutes west of UTC, so '+05:30' is -330
const addFixedOffsetZone = (timeZone: string): boolean => {
  const [, sign, hours, minutes] = FIXED_OFFSET_REGEX.exec(timeZone) ?? [];

  if (!sign) {
    return false;
  }

  const offset = (Number(hours) * 60 + Number(minutes)) * (sign === '+' ? -1 : 1);
  moment.tz.add(moment.tz.pack({ name: timeZone, abbrs: [timeZone], untils: [Infinity], offsets: [offset] }));

  return true;
};

// Intl reports a fixed offset like '+00:00' when the device has no named zone, and moment has no data for it.
// The offset is registered as its own zone so times keep their real offset; anything else falls back to UTC.
export const toMomentTimeZone = (timeZone: string): string =>
  moment.tz.zone(timeZone) || addFixedOffsetZone(timeZone) ? timeZone : 'UTC';

const getCurrentUserTimeZone = (): string => Intl.DateTimeFormat().resolvedOptions().timeZone;

export const useTimeZone = (): string => {
  const buildingTimeZone = useSelector(selectBuildingTimeZone);

  return useMemo(() => {
    return toMomentTimeZone(buildingTimeZone ?? getCurrentUserTimeZone());
  }, [buildingTimeZone]);
};
